import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { IndianRupee, ChevronDown, ChevronRight, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getRates, setFabricRate, setColorRate, setGst, formatApiError } from "@/lib/api";
import { slug, evalFormula, deriveFabricCodes, itemMatchesQuery } from "@/lib/fabricData";
import { FormulaInput } from "@/components/FormulaInput";

const fmtMoney = (n) => (n === null || n === undefined || n === "" ? "" : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 }));

export function RatesManager({ token, fabrics, stock }) {
  const [rates, setRates] = useState({ fabrics: {}, overrides: {}, gst: null });
  const [gstDraft, setGstDraft] = useState("");
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState("");
  const [drafts, setDrafts] = useState({}); // key -> raw string being typed

  const load = async () => {
    try {
      const r = await getRates(token);
      setRates(r);
      setGstDraft(r.gst === null || r.gst === undefined ? "" : String(r.gst));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const codeMap = useMemo(() => deriveFabricCodes(stock, fabrics), [stock, fabrics]);
  const colorsByFabric = useMemo(() => {
    const m = {};
    (stock || []).forEach((s) => { if (s.active !== false) (m[s.fabric] = m[s.fabric] || []).push(s); });
    return m;
  }, [stock]);

  const activeFabrics = useMemo(() => {
    const list = (fabrics || []).filter((f) => f.active !== false);
    if (!search.trim()) return list;
    // Keep a fabric if its name/code matches, or any of its colours match.
    return list.filter((f) => {
      const rows = colorsByFabric[f.name] || [];
      if (itemMatchesQuery({ fabric: f.name, color: "" }, search, codeMap)) return true;
      return rows.some((s) => itemMatchesQuery(s, search, codeMap));
    });
  }, [fabrics, search, colorsByFabric, codeMap]);

  const saveGst = async () => {
    const blank = gstDraft.trim() === "";
    const v = blank ? null : evalFormula(gstDraft);
    if (!blank && v === null) return toast.error("Enter a valid GST %");
    try { await setGst(token, v); toast.success(blank ? "GST cleared" : "GST saved"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveFabric = async (fabric, raw) => {
    const trimmed = String(raw ?? "").trim();
    const v = trimmed === "" ? null : evalFormula(trimmed);
    if (trimmed !== "" && v === null) return toast.error("Enter a valid rate");
    try {
      await setFabricRate(token, fabric, v);
      setDrafts((p) => { const n = { ...p }; delete n[`f:${fabric}`]; return n; });
      setRates((p) => ({ ...p, fabrics: { ...p.fabrics, [fabric]: v } }));
      toast.success(v === null ? "Rate cleared" : `${fabric} rate set to ₹${fmtMoney(v)}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveColor = async (fabric, color, raw) => {
    const trimmed = String(raw ?? "").trim();
    const v = trimmed === "" ? null : evalFormula(trimmed);
    if (trimmed !== "" && v === null) return toast.error("Enter a valid rate");
    try {
      await setColorRate(token, fabric, color, v);
      const key = `color:${fabric}|${color}`;
      setDrafts((p) => { const n = { ...p }; delete n[`c:${fabric}|${color}`]; return n; });
      setRates((p) => {
        const overrides = { ...p.overrides };
        if (v === null) delete overrides[`${fabric}|${color}`]; else overrides[`${fabric}|${color}`] = v;
        return { ...p, overrides };
      });
      toast.success(v === null ? "Override cleared" : `${color} set to ₹${fmtMoney(v)}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const fVal = (fabric) => drafts[`f:${fabric}`] !== undefined ? drafts[`f:${fabric}`] : (rates.fabrics[fabric] ?? "");
  const cVal = (fabric, color) => drafts[`c:${fabric}|${color}`] !== undefined ? drafts[`c:${fabric}|${color}`] : (rates.overrides[`${fabric}|${color}`] ?? "");

  return (
    <div className="space-y-5" data-testid="rates-panel">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display font-black text-2xl tracking-tight text-white">Rates</h2>
          <p className="text-sm text-white/40">Set a default ₹ rate per fabric (per its unit). Override any colour. GST applies to all orders unless overridden on the order.</p>
        </div>
        <div className="flex items-end gap-2 border border-white/10 bg-[#0A0A0A] p-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40 block mb-1">GST %</Label>
            <FormulaInput value={gstDraft} onValueChange={setGstDraft} onCommit={() => saveGst()}
              placeholder="e.g. 5 or 5+7" data-testid="rates-gst-input" className="rounded-none h-9 w-28 bg-[#141414] border-white/10 text-white text-sm focus-visible:ring-2 focus-visible:ring-white/30" />
          </div>
          <Button onClick={saveGst} data-testid="rates-gst-save" className="rounded-none h-9 bg-white text-black hover:bg-white/90 text-xs uppercase tracking-wider">Save GST</Button>
        </div>
      </div>

      <div className="relative w-full sm:w-96">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search fabric, code or colour…" data-testid="rates-search" className="rounded-none h-10 bg-[#141414] border-white/10 pl-10 pr-9 text-white text-sm" />
        {search && <button onClick={() => setSearch("")} data-testid="rates-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center text-white/40 hover:text-white"><X className="h-4 w-4" /></button>}
      </div>

      <div className="border border-white/10 divide-y divide-white/10">
        {activeFabrics.length === 0 && <div className="px-4 py-6 text-sm text-white/40">No fabrics match.</div>}
        {activeFabrics.map((f) => {
          const rows = colorsByFabric[f.name] || [];
          const isOpen = !!expanded[f.name];
          return (
            <div key={f.id || f.name} data-testid={`rates-fabric-${slug(f.name)}`}>
              <div className="flex flex-wrap items-center gap-3 px-4 py-3 bg-[#0D0D0D]">
                <button onClick={() => setExpanded((p) => ({ ...p, [f.name]: !p[f.name] }))} data-testid={`rates-fabric-toggle-${slug(f.name)}`} className="text-white/50 hover:text-white shrink-0">
                  {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
                <span className="flex-1 min-w-0 truncate text-sm font-medium text-white">{f.name} <span className="text-white/40 text-xs">· {f.unit || "kg"} · {rows.length} colour(s)</span></span>
                <div className="flex items-center gap-2">
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40">Default ₹/{f.unit || "kg"}</Label>
                  <div className="relative">
                    <IndianRupee className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/30" />
                    <FormulaInput value={fVal(f.name)} onValueChange={(val) => setDrafts((p) => ({ ...p, [`f:${f.name}`]: val }))}
                      onCommit={(v, raw) => saveFabric(f.name, raw)}
                      placeholder="—" data-testid={`rates-fabric-input-${slug(f.name)}`} className="rounded-none h-9 w-32 pl-7 bg-[#141414] border-white/10 text-white text-sm focus-visible:ring-2 focus-visible:ring-white/30" />
                  </div>
                </div>
              </div>
              {isOpen && (
                <div className="divide-y divide-white/5 bg-[#0A0A0A]">
                  {rows.length === 0 && <div className="px-6 py-3 text-xs text-white/40">No colours under this fabric.</div>}
                  {rows.map((s) => {
                    const override = rates.overrides[`${f.name}|${s.color}`];
                    const effective = override ?? rates.fabrics[f.name];
                    return (
                      <div key={s.id || s.color} className="flex items-center gap-3 px-6 py-2" data-testid={`rates-color-row-${slug(f.name)}-${slug(s.color)}`}>
                        <span className="h-4 w-4 border border-white/20 shrink-0" style={{ backgroundColor: s.hex || "#666" }} />
                        <span className="flex-1 min-w-0 truncate text-sm text-white/80">{s.color}</span>
                        <span className="text-[11px] text-white/40 tabular hidden sm:inline">Effective: {effective === null || effective === undefined ? "—" : `₹${fmtMoney(effective)}`}</span>
                        <div className="relative">
                          <IndianRupee className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/30" />
                          <FormulaInput value={cVal(f.name, s.color)} onValueChange={(val) => setDrafts((p) => ({ ...p, [`c:${f.name}|${s.color}`]: val }))}
                            onCommit={(v, raw) => saveColor(f.name, s.color, raw)}
                            placeholder="override" data-testid={`rates-color-input-${slug(f.name)}-${slug(s.color)}`} className="rounded-none h-8 w-28 pl-7 bg-[#141414] border-white/10 text-white text-sm focus-visible:ring-2 focus-visible:ring-white/30" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
